/*
 * The done-fencer grace period (direction §5) — CLIENT math, by design.
 *
 * §5 is explicit about the split: "API supplies facts (done: placed/scratched/
 * excluded, with timestamps) and the per-user preference; the client computes the
 * row placement, keeping the behavior consistent across future native clients."
 * So the server keeps sending a done fencer's card indefinitely, and this module
 * decides when it leaves. A server that filtered them out would make the grace
 * window unimplementable, which is why the Go side has a test pinning that it
 * does not.
 *
 * WHAT THE WINDOW IS FOR. A fencer finishing is the moment a coach most wants to
 * see the result, and the moment the app is most tempted to make the row vanish
 * mid-glance. Ten seconds of "where did it go" costs more than ten minutes of a
 * settled row. So the outcome replaces the strip IN THE SAME SLOT — no layout
 * jump, no new section — and the card leaves later, visibly.
 *
 * THERE IS NO DONE SECTION and no countdown. §5: "There is no Done section;
 * outcomes live in the Past tab", and the ten minutes runs with "no countdown
 * text" — a timer would turn a settled fact into something to watch.
 *
 * THE VOCABULARY LADDER, quoted, because the rungs are the spec:
 *   "Eliminated" (done, place unknown)
 *   → "Finished 14th" / "Finished 3rd (tie)" (place known)
 * No denominator, ever: field size is not reliably owned data (contract §8), so
 * "3rd of 40" is unavailable rather than merely omitted.
 */

import { minutesSince } from "./staleness";

/** §5's default window. */
export const GRACE_MINUTES = 10;

/**
 * When a done fencer's card leaves.
 *
 * §14 lists this as a FUTURE setting ("leave immediately / after 10 minutes /
 * when event completes"), so there is no server field for it yet and the app
 * always passes the default. The three cases are implemented anyway because they
 * are what the math IS — and because a preference that arrives later should be a
 * new column read into an existing parameter, not a rewrite of this module.
 */
export type GracePolicy = "leave-immediately" | "after-grace" | "when-event-completes";

export const DEFAULT_GRACE_POLICY: GracePolicy = "after-grace";

/** The done-facts this module reads. A subset of ActiveFencer, so tests need no fixture. */
export interface DoneFacts {
  outcome?: "placed" | "scratched" | "excluded" | undefined;
  outcome_observed_at?: string | undefined;
  place?: number | undefined;
  tie: boolean;
  /**
   * THE SECOND DONE SIGNAL (decision 459), and the earlier one: the bracket
   * names the winner of a bout as soon as it is entered, where a place waits
   * for the whole table to close.
   *
   * Deliberately not folded into `outcome`. Decision 247's rule is that the
   * FIRST reason a fencer is done wins, and an `eliminated` outcome value would
   * land first and then have to be overruled by the placement — so the two
   * facts stay apart and this module reads both.
   */
  eliminated_table_size?: number | undefined;
  eliminated_table_raw?: string | undefined;
  eliminated_at?: string | undefined;
}

/**
 * Whether this fencer is finished at all.
 *
 * Typed on the one field it reads, because Upcoming's done-cascade (decision
 * 357) asks the same question of rows that carry an outcome and nothing else —
 * "done" has to mean one thing across the two views, so both ask it here.
 */
export function isDone(f: Partial<DoneFacts>): boolean {
  return f.outcome != null || f.eliminated_at != null || f.eliminated_table_raw != null;
}

/**
 * When this fencer became done: the EARLIER of the two signals.
 *
 * Which is the whole reason the grace window reads this instead of
 * `outcome_observed_at`. An elimination lands when the opponent advances and a
 * placement lands when the table closes — forty minutes later on a real
 * weekend — so measuring from the later one would put a card back on screen
 * that had already left, ten minutes after the reader watched it go.
 */
export function doneAt(f: Partial<DoneFacts>): string | null {
  const stamps = [f.outcome_observed_at, f.eliminated_at].filter(
    (s): s is string => typeof s === "string",
  );
  if (stamps.length === 0) return null;
  return stamps.reduce((a, b) => (new Date(a) < new Date(b) ? a : b));
}

/**
 * Whether a card still belongs on screen.
 *
 * A fencer who is not done always stays. A done fencer stays for the window, then
 * goes. Note what happens with a MISSING `outcome_observed_at`: the card stays.
 * The timestamp is nullable because migration 0012 backfilled historical results
 * without one, and a null there means "we do not know when this finished" — for
 * which the safe reading is to keep showing it, since the alternative silently
 * hides a live fencer whose result row happens to predate the column.
 */
export function isVisible(
  f: DoneFacts,
  policy: GracePolicy = DEFAULT_GRACE_POLICY,
  now: Date = new Date(),
  eventComplete = false,
): boolean {
  if (!isDone(f)) return true;

  switch (policy) {
    case "leave-immediately":
      return false;
    case "when-event-completes":
      return !eventComplete;
    case "after-grace": {
      const at = doneAt(f);
      if (!at) return true;
      return minutesSince(at, now) < GRACE_MINUTES;
    }
  }
}

/**
 * Whether the app SHELL should still offer the Active tab (decision 546).
 *
 * THE TAB DECAYS WITH THE FENCERS, NOT WITH THE CALENDAR, and it is in this
 * module so that it decays at the same rate by construction: this is the same
 * `GRACE_MINUTES` and the same `minutesSince` that retire a card in `isVisible`
 * above, which is the whole reason the rule lives here rather than in the shell.
 *
 * WHAT WENT WRONG WITHOUT IT. The tab rode on `active_event_count`, whose only
 * time bound is the tournament date window — so a weekend that ended on Sunday
 * night still offered a tab all through Monday, onto a screen this module had
 * emptied ten minutes after the last fencer finished. The count answers "is a
 * tracked tournament running"; the tab is asking "is anybody still on screen".
 *
 * TWO FACTS IN, ONE DECISION OUT, per contract §0. The server sends the
 * population that does NOT age out (`activeFencerCount`: not done and their
 * event's venue day is not over, or done with no timestamp to age from) and the
 * instant the rest of them finished; the arithmetic on the second one is this
 * client's, exactly as it is for a card.
 *
 * NEITHER FACT IS EVER SYNTHESISED ON A FAILED REQUEST — decision 324's rule,
 * enforced where it has to be, in `wakeup.ts`: a request that failed is not
 * evidence that nobody is fencing, and a zero here would take the tab away at a
 * venue on the morning it is the entire point of the product.
 */
export function activeTabVisible(
  facts: { activeFencerCount: number; lastFencerDoneAt?: string | undefined },
  now: Date = new Date(),
): boolean {
  if (facts.activeFencerCount > 0) return true;
  const at = facts.lastFencerDoneAt;
  if (!at) return false;
  return minutesSince(at, now) < GRACE_MINUTES;
}

/**
 * The outcome's label, as i18n pieces rather than text.
 *
 * Returns a key plus params because the placement is an ORDINAL and ordinals are
 * locale data (§7): English suffixes are -st/-nd/-rd/-th, Russian inflects, and
 * Chinese prefixes 第. i18next's `ordinal: true` selects the right plural form
 * from the locale's own rules, which a formatted string here would foreclose.
 *
 * The `tie` flag travels separately rather than being folded into the phrase,
 * because §11 renders it as a DE-EMPHASIZED suffix — "3rd (tie)", never "3T",
 * since USA Fencing's T-notation does not localize and misreads beside an
 * ordinal. Two pieces because they are styled differently.
 */
export type OutcomeLabel =
  | { kind: "eliminated"; key: "active.outcome.eliminated" }
  | { kind: "scratched"; key: "active.outcome.scratched" }
  | { kind: "excluded"; key: "active.outcome.excluded" }
  | { kind: "placed"; key: "active.outcome.finished"; place: number; tie: boolean }
  | { kind: "outIn"; key: string; table: number }
  | { kind: "outInUnclear"; key: "active.outcome.outInUnclear"; raw: string };

export function outcomeLabel(f: DoneFacts): OutcomeLabel | null {
  switch (f.outcome) {
    case undefined:
      // THE ELIMINATION ALONE, which is the state this rung exists for: the
      // bracket says she is out and no placement has landed yet (decision
      // 459). More informative than "Eliminated" — it says WHERE — and it is
      // stable, where the placement's denominator is the value decision 442
      // watched drift.
      if (f.eliminated_table_size != null) {
        const key =
          f.eliminated_table_size === 2
            ? "active.outcome.outInFinal"
            : f.eliminated_table_size === 4
              ? "active.outcome.outInSemis"
              : "active.outcome.outInTable";
        return { kind: "outIn", key, table: f.eliminated_table_size };
      }
      // A heading we have no word for: §9 bars using the raw string as a
      // label, so the rung says only that she is out and the disclosure
      // quotes FTL.
      if (f.eliminated_table_raw) {
        return {
          kind: "outInUnclear",
          key: "active.outcome.outInUnclear",
          raw: f.eliminated_table_raw,
        };
      }
      return null;
    case "scratched":
      return { kind: "scratched", key: "active.outcome.scratched" };
    case "excluded":
      return { kind: "excluded", key: "active.outcome.excluded" };
    case "placed":
      // The ladder's two rungs. A placed fencer with no place is "Eliminated" —
      // done, and we genuinely do not know where they came. Inventing a place, or
      // rendering an empty slot, are the two things §5 rules out.
      if (f.place == null) return { kind: "eliminated", key: "active.outcome.eliminated" };
      return {
        kind: "placed",
        key: "active.outcome.finished",
        place: f.place,
        tie: f.tie,
      };
  }
}
